import React from 'react';

const tips = [
  {
    title: "1. Max Out Your Roth IRA",
    body: `
      A Roth IRA is an invaluable investment tool because it allows your money to grow tax-free. The contribution limit for 2025 is $6,000 ($7,000 if you're over 50), and if you invest early, this can grow into a substantial amount by the time you're ready to retire.

      One of the best aspects of a Roth IRA is that your investments grow **tax-free**, and when you withdraw them during retirement, you don’t owe any taxes on the earnings. This is a huge advantage, especially when you compound your money over several decades.

      **Example**: If you start contributing $500 a month to a Roth IRA and earn an average annual return of 7%, you'll have more than **$1 million** by age 60. That's tax-free income! It's one of the simplest, most effective retirement strategies available, and it's especially beneficial for those who are 30 or younger.

      The goal here is to maximize your contributions, which will create a **tax-free nest egg** over the years. It's also worth noting that if you're in a high tax bracket now, this is an excellent way to shelter your money from taxes while you earn.

      **Recommended Asset Allocation**: 
      - For a stable growth option, consider a broad market ETF like the **Vanguard Total Stock Market ETF (VTI)** or **Schwab U.S. Large-Cap ETF (SCHX)**, which have consistently outperformed inflation over time and offer diversification across sectors.
    `,
    linkText: "Learn More About Roth IRA →",
    link: "https://www.schwab.com/ira/roth-ira"
  },
  {
    title: "2. Automate Your Investments",
    body: `
      Life can get busy, and the idea of manually managing your investments can be daunting, but **automating your investments** is a game-changer. Using **automatic contributions** to your investment account ensures you're consistently adding to your portfolio, even when you forget about it.

      **Dollar-Cost Averaging (DCA)** is a powerful technique here. It’s where you invest a fixed amount (e.g., $200 every month) regardless of market conditions. Over time, this strategy reduces the risk of investing all your money during a market peak or missing out when the market dips.

      **Why It Works**:
      - It removes the emotional component from investing, meaning you're not tempted to panic-sell or get greedy during market swings.
      - DCA lowers the average cost of your investments over time, especially in volatile markets.

      If you're investing in **individual stocks** or **ETFs**, automate your investments into those. For example, if you wanted to invest in **Apple** or **Amazon**, you could set up an automatic purchase each month, regardless of whether the stock price is high or low.

      **Real Example**: Let's say you invest $500 monthly into **Amazon (AMZN)** for 10 years, assuming an annual return of 15% (which Amazon has averaged over the past decade). That $500 a month could grow into **$220,000** after 10 years. The key here is the **consistency** and long-term compound growth.

      **Recommended Platform**: 
      - Consider **Fidelity** or **Vanguard**, which both allow automatic investment options and have low-cost, diversified funds.
    `,
    linkText: "Start Automating Your Investments →",
    link: "https://www.robinhood.com"
  },
  {
    title: "3. Buy ETFs — Not Single Stocks",
    body: `
      If you're looking for long-term, **consistent growth** with lower risk, **ETFs** are an ideal choice. Unlike individual stocks, ETFs hold a **basket** of different stocks, which provides instant **diversification**. This helps protect you from individual stock volatility.

      **What’s the appeal of ETFs?**
      - **Low risk**: With ETFs like the **S&P 500** ETF (**SPY**) or the **Vanguard Total Stock Market ETF (VTI)**, you’re essentially buying shares of a **large number of companies**, reducing the impact of any one company’s poor performance.
      - **Low fees**: ETFs typically have **lower expense ratios** than mutual funds, making them cost-effective.
      - **Long-term returns**: Historically, broad market ETFs like **SPY** (S&P 500 ETF) have returned an **average annualized return of 7-10%** over long periods, making them solid performers for those looking to grow wealth over time.

      **Example**: If you invested $500 monthly in **SPY** over the past 20 years, that would have turned into approximately **$430,000** (a 7-8% annual return, compounded). This highlights the benefit of sticking with the **S&P 500**, which is the benchmark for U.S. large-cap stocks.

      **Sector-Specific ETFs**: For higher risk and potentially higher returns, you can focus on specific sectors like technology, healthcare, or green energy. For instance, **Technology Select Sector SPDR Fund (XLK)** focuses on tech stocks like **Apple** and **Microsoft**, both of which have significantly outperformed the broader market.

      **Why It Works**:
      - ETFs are low-cost, diversified, and historically strong performers.
      - They mitigate the risk of individual stocks and give you broad exposure to the market.
    `,
    linkText: "Explore ETFs →",
    link: "https://investor.vanguard.com/investment-products/list/etfs"
  },
  {
    title: "4. Invest in Yourself — High ROI Skills & Education",
    body: `
      **Investing in your career** and acquiring new, in-demand skills can yield one of the highest **ROI** (Return on Investment) in your 30s. While the stock market and crypto can grow your wealth, **upskilling** is a form of investment that offers immediate financial returns through higher salaries and career advancement.

      **Top Skills to Invest In**:
      - **Data Science & Analytics**: Data scientists have a starting salary around **$90,000**, with experienced ones making upwards of **$150,000**. The tech industry needs data-driven decision-makers, making this skill incredibly valuable.
      - **Machine Learning & AI**: As industries continue to automate, knowledge of **AI** can lead to high-paying roles. AI-related positions have been growing rapidly and offer salaries over **$120,000**.
      - **Project Management**: For those in business or tech, project management certifications (like **PMP**) can help you transition into management roles with significant salary increases.

      **Example**: Learning a programming language like **Python** and completing a data science bootcamp could potentially raise your salary from $60,000 to $120,000 within a couple of years, yielding an ROI that far exceeds anything the stock market can provide in the same timeframe.

      **Why It Works**:
      - Upskilling makes you more marketable and can significantly raise your earning potential.
      - The **compounding** effect of career growth leads to increasing salaries over time.

      **Recommended Action**:
      - Consider online courses on platforms like **Coursera**, **Udemy**, or **LinkedIn Learning** for high-demand skills in tech, finance, and marketing.
    `,
    linkText: "Discover Top Courses →",
    link: "https://www.udemy.com"
  },
  {
    title: "5. Diversify with Crypto & Alternatives",
    body: `
      **Cryptocurrency** and alternative assets have emerged as significant wealth-building tools, especially for those in their 30s. While **crypto** can be volatile, it has shown the potential for explosive growth and serves as a hedge against inflation.

      **Bitcoin (BTC)** is often referred to as “**digital gold**” due to its limited supply (only 21 million coins). It has been **widely recognized** as a store of value, similar to gold, and **institutional investors** have been flocking to Bitcoin in recent years. Over the past decade, Bitcoin has appreciated by an average of **200-300% annually**.

      **Ethereum (ETH)** powers decentralized applications (dApps) and smart contracts, making it more than just a store of value. It’s the backbone of the **DeFi (Decentralized Finance)** space and has the potential for even greater growth as the ecosystem expands.

      **Alternatives**: Beyond crypto, consider investing in **Real Estate Investment Trusts (REITs)** or **precious metals** like gold, which have historically been strong performers during market downturns.

      **Why It Works**:
      - **Bitcoin** offers explosive growth potential, though it’s volatile. Long-term holders have seen significant returns.
      - **Ethereum** plays a key role in the emerging **blockchain-based economy**, making it a strong investment for the future.
      - **REITs** offer exposure to real estate without the need for direct property ownership, and they typically offer high yields.

      **Real Example**:
      - Bitcoin grew from **$1,000 to $60,000** in just 5 years (2017-2021), demonstrating the growth potential. However, Bitcoin can also be volatile, so it's essential to stay informed and only invest what you're willing to hold long-term.

      **Recommended Crypto Platforms**: Platforms like **Coinbase** and **Binance** are ideal for purchasing and storing cryptocurrency securely. For those new to crypto, **Coinbase** offers a user-friendly platform, while **Binance** offers more advanced features for seasoned traders.
    `,
    linkText: "Explore Crypto Opportunities →",
    link: "https://www.coinbase.com"
  }
];

const TipCard = ({ title, body, linkText, link }) => (
  <div style={styles.card}>
    <h3 style={styles.cardTitle}>{title}</h3>
    <p style={styles.cardBody}>{body}</p>
    <a href={link} style={styles.cardLink}>{linkText}</a>
  </div>
);

const SmartInvesting30s = () => {
  return (
    <div style={styles.container}>
      <p style={styles.intro}>
        Your 30s are the most powerful decade for building wealth. Why? Because you have time on your side — and compounding rewards early action.
        Here are five smart, realistic ways to grow your future and retire rich, not broke.
      </p>

      {tips.map((tip, index) => (
        <TipCard key={index} {...tip} />
      ))}

      <div style={styles.footer}>
        <h2 style={styles.footerTitle}>💡 Final Thought</h2>
        <p style={styles.footerBody}>
          You don’t need to be rich to start investing — but you do need to start. Automate the process, stay consistent, and diversify.
          Your future self will thank you.
        </p>
        
      </div>
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    lineHeight: '1.6',
    backgroundColor: '#f4f4f9',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  },
  intro: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '25px',
    textAlign: 'center'
  },
  card: {
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '25px',
    backgroundColor: '#fff',
    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.1)'
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: '600',
    marginBottom: '10px',
    color: '#0070f3',
  },
  cardBody: {
    fontSize: '16px',
    color: '#555',
    lineHeight: '1.6',
  },
  cardLink: {
    display: 'inline-block',
    marginTop: '15px',
    textDecoration: 'none',
    color: '#0070f3',
    fontWeight: 'bold',
    fontSize: '16px',
  },
  footer: {
    marginTop: '40px',
    textAlign: 'center'
  },
  footerTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#333'
  },
  footerBody: {
    fontSize: '16px',
    color: '#555',
    marginTop: '10px',
    marginBottom: '20px',
  },
  cta: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#fff',
    backgroundColor: '#0070f3',
    padding: '10px 20px',
    borderRadius: '5px',
    textDecoration: 'none'
  }
};

export default SmartInvesting30s;
